import { API_BASE_URL } from '../../constants/api';
import { io, Socket } from 'socket.io-client';

export interface WebSocketMessage {
  type: string;
  payload: any;
  timestamp: string;
}

export interface PermissionChangeEvent {
  type: 'PERMISSION_CHANGED' | 'ROLE_UPDATED' | 'MODULE_UPDATED';
  userId?: string;
  roleId?: string;
  moduleId?: string;
  permissionId?: string;
  action: 'ADDED' | 'REMOVED' | 'UPDATED';
  details: {
    roleName?: string;
    moduleName?: string;
    permissionName?: string;
    message: string;
  };
}

export interface NewOrderEvent {
  type: 'NEW_ORDER';
  pedidoId: string;
  numeroPedido: string;
  clienteNombre: string;
  clienteEmail: string;
  total: number;
  estado: string;
}

export interface OrderStatusChangeEvent {
  type: 'ORDER_STATUS_CHANGE';
  pedidoId: string;
  numeroPedido: string;
  estado: string;
  estadoAnterior: string;
  clienteEmail?: string;
}

export type WebSocketEventHandler = (event: PermissionChangeEvent | NewOrderEvent | OrderStatusChangeEvent) => void;
export type NewOrderHandler = (event: NewOrderEvent) => void;
export type OrderStatusChangeHandler = (event: OrderStatusChangeEvent) => void;

class WebSocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 3000;
  private isReconnecting = false;
  private eventHandlers: Map<string, WebSocketEventHandler[]> = new Map();
  private connectionStatus: 'connected' | 'connecting' | 'disconnected' | 'error' = 'disconnected';
  private statusListeners: Array<(status: typeof this.connectionStatus) => void> = [];

  connect(token?: string): Promise<void> {
    // Si ya hay un socket conectado o conectándose, no crear otro
    if (this.socket?.connected) {
      return Promise.resolve();
    }
    if (this.socket && this.connectionStatus === 'connecting') {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        const serverUrl = API_BASE_URL.replace('/api/v1', '');
        
        this.setConnectionStatus('connecting');
        this.socket = io(serverUrl + '/ws', {
          auth: {
            token: token
          },
          transports: ['websocket', 'polling'],
          timeout: 10000,
          autoConnect: false
        });

        this.socket.on('connect', () => {
          this.reconnectAttempts = 0;
          this.isReconnecting = false;
          this.setConnectionStatus('connected');
          resolve();
        });

        this.socket.on('connection_established', (data) => {
        });

        this.socket.on('message', (message: WebSocketMessage) => {
          this.handleMessage(message);
        });

        this.socket.on('disconnect', (reason) => {
          this.setConnectionStatus('disconnected');
          
          if (!this.isReconnecting && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.isReconnecting = true;
            this.scheduleReconnect();
          } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.setConnectionStatus('error');
          }
        });

        this.socket.on('connect_error', (error) => {
          console.error('🔌 Socket.IO error:', error);
          this.setConnectionStatus('error');
          reject(error);
        });

        this.socket.on('auth_error', (error) => {
          console.error('🔌 Authentication error:', error);
          this.setConnectionStatus('error');
          reject(error);
        });

        this.socket.connect();

      } catch (error) {
        console.error('🔌 Error creating Socket.IO:', error);
        this.setConnectionStatus('error');
        reject(error);
      }
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.setConnectionStatus('disconnected');
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;

    setTimeout(() => {
      const token = localStorage.getItem('accessToken');
      this.connect(token || undefined)
        .catch(() => {
          this.isReconnecting = false;
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        });
    }, this.reconnectInterval);
  }

  private handleMessage(message: WebSocketMessage): void {
    // Notificar handlers de tipo específico
    if (message.type === 'PERMISSION_CHANGE') {
      const event: PermissionChangeEvent = message.payload;
      this.notifyHandlers('permission_change', event);
    }
    
    if (message.type === 'NEW_ORDER') {
      const event: NewOrderEvent = message.payload;
      this.notifyHandlers('new_order', event);
    }
    
    if (message.type === 'ORDER_STATUS_CHANGE') {
      const event: OrderStatusChangeEvent = message.payload;
      this.notifyHandlers('order_status_change', event);
    }
    
    if (message.type === 'ORDER_CHAT_MESSAGE') {
      this.notifyHandlers('order_chat_message', message.payload);
    }
    
    // Notificar handlers genéricos de 'message' para cualquier tipo
    this.notifyHandlers('message', message);
  }

  private notifyHandlers(eventType: string, event: any): void {
    const handlers = this.eventHandlers.get(eventType) || [];
    handlers.forEach(handler => {
      try {
        handler(event);
      } catch (error) {
        console.error('Error in WebSocket event handler:', error);
      }
    });
  }

  addEventListener(eventType: 'permission_change', handler: WebSocketEventHandler): void;
  addEventListener(eventType: 'new_order', handler: NewOrderHandler): void;
  addEventListener(eventType: 'order_status_change', handler: OrderStatusChangeHandler): void;
  addEventListener(eventType: string, handler: any): void {
    const handlers = this.eventHandlers.get(eventType) || [];
    handlers.push(handler);
    this.eventHandlers.set(eventType, handlers);
  }

  removeEventListener(eventType: 'permission_change', handler: WebSocketEventHandler): void;
  removeEventListener(eventType: 'new_order', handler: NewOrderHandler): void;
  removeEventListener(eventType: 'order_status_change', handler: OrderStatusChangeHandler): void;
  removeEventListener(eventType: string, handler: any): void {
    const handlers = this.eventHandlers.get(eventType) || [];
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
      this.eventHandlers.set(eventType, handlers);
    }
  }

  addStatusListener(listener: (status: typeof this.connectionStatus) => void): void {
    this.statusListeners.push(listener);
    // Enviar el estado actual inmediatamente
    listener(this.connectionStatus);
  }

  removeStatusListener(listener: (status: typeof this.connectionStatus) => void): void {
    const index = this.statusListeners.indexOf(listener);
    if (index > -1) {
      this.statusListeners.splice(index, 1);
    }
  }

  private setConnectionStatus(status: typeof this.connectionStatus): void {
    this.connectionStatus = status;
    this.statusListeners.forEach(listener => {
      try {
        listener(status);
      } catch (error) {
        console.error('Error in status listener:', error);
      }
    });
  }

  getConnectionStatus(): typeof this.connectionStatus {
    return this.connectionStatus;
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  send(message: any): void {
    if (this.isConnected()) {
      this.socket?.emit('message', message);
    } else {
      console.warn('🔌 No se puede enviar mensaje: Socket.IO no conectado');
    }
  }
}

// Instancia singleton
export const webSocketService = new WebSocketService();